import { createContext, useContext, useId, useLayoutEffect } from 'react';

export interface EditState { dirty: boolean; busy: boolean }
export interface EditBoundaryValue extends EditState {
  request: (action: () => void, onCancel?: () => void) => boolean;
  /** Approved synchronous actions bypass a second prompt; pending writes never do. */
  isNavigationBlocked: () => boolean;
}
export interface EditContextValue extends EditBoundaryValue {
  report: (id: string, state: EditState | null) => void;
  approveChild: (id: string, action: () => void) => void;
}
export const EditContext = createContext<EditContextValue | null>(null);

/** Reports mounted editors to their nearest boundary; the returned id scopes nested approval. */
export function useReportEdits(dirty: boolean, busy = false): string {
  const report = useContext(EditContext)?.report;
  const id = useId();
  useLayoutEffect(() => { report?.(id, { dirty, busy }); }, [report, id, dirty, busy]);
  useLayoutEffect(() => () => report?.(id, null), [report, id]);
  return id;
}

export function useEditBoundary(): EditBoundaryValue | null { return useContext(EditContext); }
